/* global Sentry */

// Reporting for stage-submit failures (apply form, online form, quiz, scheduling).
//
// These pages had no instrumentation at all: page-scheduling.js discarded the error object,
// page-online-form.js and page-online-quiz.js swallowed it, and only page-applicant.js reached
// Sentry -- accidentally, by rethrowing into an unhandled rejection. That is why MW-2325's ask to
// "pull Sentry for what is behind the toast" could not be answered, and why a triage thread in
// #p-job-seeker-feedback closed with "we don't track any error logs from it". [MW-2325]
//
// Nothing here may carry PII. Digest keys and the refill `t` token are credentials -- possessing
// the link is sufficient to act on the record -- and validation messages routinely echo the
// applicant's own email or phone. So we send lengths, key names and booleans, never values.

const MAX_BUFFERED_ERRORS = 10;

function inAppWebview() {
  try {
    return String(window.location.search).indexOf('from=ws_app') !== -1;
  } catch (e) {
    return false;
  }
}

function tagValue(value) {
  if (value === undefined || value === null || value === '') return 'none';
  return String(value);
}

// Key names only; the values are applicant answers.
function fieldErrorKeys(fieldErrors) {
  if (!fieldErrors || typeof fieldErrors !== 'object') return undefined;
  const keys = Object.keys(fieldErrors);
  return keys.length ? keys : undefined;
}

function buildReport(classified, context) {
  const ctx = context || {};
  const { serverMessage } = classified;

  return {
    page: ctx.page,
    action: ctx.action || 'submit',
    kind: classified.kind,
    status: classified.status,
    errorCode: classified.errorCode,
    state: classified.state,
    retryable: classified.retryable,
    hasServerMessage: !!serverMessage,
    // The length, never the text.
    serverMessageLength: serverMessage ? serverMessage.length : 0,
    fieldErrorKeys: fieldErrorKeys(classified.fieldErrors),
    digestKeyPresent: !!ctx.digestKeyPresent,
    locale: ctx.locale,
    inAppWebview: inAppWebview(),
  };
}

// The axios error hangs the whole response off error.response.data, so handing it straight to
// Sentry would ship the very validation text we are careful not to put in `extra` -- and those
// messages echo the applicant's own email and phone. Send a synthetic error instead, keeping the
// original stack (whose header is just "AxiosError: Request failed with status code NNN").
function sanitizedError(error, report) {
  const sanitized = new Error(
    `Stage submit failed: ${report.page || 'unknown'} ${report.kind} ${tagValue(report.status)}`,
  );
  sanitized.name = 'StageSubmitError';
  if (error && typeof error.stack === 'string') sanitized.stack = error.stack;
  return sanitized;
}

/**
 * Report a classified stage-submit failure.
 *
 * Safe to call when Sentry is absent -- it is only injected when DEPLOY_ENV !== 'local'
 * (views/includes/commonHead.pug), so it is missing in local dev and under e2e.
 *
 * @param {Error} error      the original axios error
 * @param {object} classified  the result of classifySubmitError()
 * @param {object} context   { page, action, locale, digestKeyPresent }
 * @returns {object|undefined} the non-PII report, for tests
 */
export function captureStageError(error, classified, context) {
  if (!classified || classified.kind === 'canceled') return undefined;

  const report = buildReport(classified, context);

  // The e2e seam: Sentry is absent under DEPLOY_ENV=local, so specs assert against this buffer.
  try {
    if (!window.wsStageErrors) window.wsStageErrors = [];
    window.wsStageErrors.push(report);
    while (window.wsStageErrors.length > MAX_BUFFERED_ERRORS) window.wsStageErrors.shift();
  } catch (e) {
    // A page without a writable window is not worth failing a submit over.
  }

  try {
    if (typeof Sentry === 'undefined' || !Sentry.captureException) return report;

    Sentry.captureException(sanitizedError(error, report), {
      level: 'error',
      tags: {
        'ws.page': tagValue(report.page),
        'ws.action': tagValue(report.action),
        'ws.error_kind': tagValue(report.kind),
        'ws.http_status': tagValue(report.status),
        'ws.error_code': tagValue(report.errorCode),
        // Answers the question the ticket actually asks: was the applicant shown generic copy
        // because the server said nothing, or because we threw its message away?
        'ws.has_server_message': String(report.hasServerMessage),
        'ws.in_app_webview': String(report.inAppWebview),
      },
      extra: {
        serverMessageLength: report.serverMessageLength,
        fieldErrorKeys: report.fieldErrorKeys,
        digestKeyPresent: report.digestKeyPresent,
        state: report.state,
        retryable: report.retryable,
        locale: report.locale,
        useragent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
      },
      // Without this every page's failures collapse into one "AxiosError: Request failed with
      // status code 422" issue, which is how the current one hides four different problems.
      fingerprint: [
        'ws-stage-submit',
        tagValue(report.page),
        tagValue(report.kind),
        tagValue(report.status),
      ],
    });
  } catch (e) {
    // Reporting must never break the page it is reporting about.
  }

  return report;
}

/** Clear the buffered reports. Used by tests; harmless in the browser. */
export function resetStageErrors() {
  try {
    window.wsStageErrors = [];
  } catch (e) {
    // no-op
  }
}
